import { get } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Col, Row } from "reactstrap";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import Icon, { IconName } from "src/components/Icon/Icon";
import { IRootState } from "src/store/reducers";
import {
  BrokerTask,
  fetchBrokerTask,
  fetchBrokerTaskOptions,
  fetchBrokerTasks,
  saveBrokerTask,
  toggleBrokerUploadToast,
} from "src/store/reducers/brokerTasks";
import { Container } from "../../../components/Container/Container";
import DisTable from "../../../components/TableV2/Table";
import Toast from "../../../components/Toast/Toast";
import * as S from "../../../constants/StringConstants";
import { formatDate } from "../../CampaignDetail/utils";
import ".././BrokerContainer/BrokerContainer.scss";
import { BrokerTasksForm } from "./BrokerTaskForm";

const columns = (selectRequiredOriginList, selectCarrierTypeList) => {
  return [
    {
      key: "carrierName",
      title: S.BRO_TASKS_COL_CARRIER,
      style: { minWidth: "5rem" },
      render: (item: BrokerTask) => selectCarrierTypeList?.find(o => o.value === item.carrierId)?.key,
    },
    {
      key: "requirementOf",
      title: S.BRO_TASKS_COL_REQ_OF,
      style: { minWidth: "5rem" },
      render: (item: BrokerTask) => selectRequiredOriginList?.find(o => o.value === item.requirementOriginCode)?.key,
    },
    {
      key: "effective",
      title: S.BRO_TASKS_COL_EFECTIVE,
      style: { minWidth: "5rem" },
      render: (item: BrokerTask) => formatDate(item.effectiveDate),
    },
    {
      key: "expired",
      title: S.BRO_TASKS_COL_EXPIRED,
      style: { minWidth: "5rem" },
      render: (item: BrokerTask) => formatDate(item.expirationDate),
    },
    {
      key: "received",
      title: S.BRO_TASKS_COL_RECEIVED,
      style: { minWidth: "5rem" },
      render: (item: BrokerTask) => formatDate(item.receivedDate),
    },
    {
      key: "ordered",
      title: S.BRO_TASKS_COL_ORDERED,
      style: { minWidth: "5rem" },
      render: (item: BrokerTask) => formatDate(item.orderedDate),
    },
    {
      key: "followUp",
      title: S.BRO_TASKS_COL_FOLLOW_UP,
      style: { minWidth: "5rem" },
      render: (item: BrokerTask) => formatDate(item.followUpDate),
    },
    {
      key: "description",
      title: S.BRO_TASKS_COL_DESCRIPTION,
      style: { minWidth: "5rem", maxWidth: "25rem", whiteSpace: "normal", wordBreak: "break-word" },
    },
  ];
};

const BrokerTasks = () => {
  const listRef = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [rowData, setRowData] = useState<BrokerTask | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { id: brokerID } = useParams<{ id: string }>();

  const dispatch = useDispatch();

  useEffect(() => {
    const loadTask = async () => {
      if (rowData?.id) {
        setLoading(true);
        await dispatch(fetchBrokerTask(rowData.id));
        setLoading(false);
        setIsModalOpen(true);
      } else {
        await dispatch(fetchBrokerTaskOptions());
      }
    };

    loadTask();
  }, [rowData?.id]);

  const {
    task,
    isUploadToastVisible,
    options: { selectRequiredOriginList, selectCarrierTypeList, selectStatusTypeList, selectTaskCodeTypeList },
  } = useSelector((state: IRootState) => state.brokerTasks);

  const handleSave = async (data: BrokerTask) => {
    setLoading(true);

    try {
      try {
        await dispatch(saveBrokerTask(brokerID, data));
      } catch (e) {
        alert(S.BRO_DOC_UPLOAD_ERROR);
      } finally {
        setLoading(false);
        setIsModalOpen(false);
        listRef.current?.update({});
      }
    } catch (e) {}
  };

  const handleRowClick = (task: BrokerTask) => {
    setRowData(task);
    setIsModalOpen(true);
  };

  return (
    <>
      <main className="broker-tab__wrap">
        <section className="broker-tab__container">
          <Container>
            <div className="broker-tab__form-wrap">
              <Row>
                <Col sm="9" className="doc-tab__title">
                  <h1 className="form-title">{S.BRO_TASKS_TITLE}</h1>
                </Col>

                <Col sm="3" align="right">
                  <button
                    className="button-v2 button-v2__primary broker-tab__action-button"
                    onClick={() => {
                      setRowData(null);
                      setIsModalOpen(true);
                    }}>
                    <Icon name={IconName.Plus} />
                    {S.BRO_DOC_ADD_TASK_BUTTON}
                  </button>
                </Col>
              </Row>

              <Row>
                <Col className="doc-tab__table-override">
                  <DisTable<BrokerTask>
                    columns={columns(selectRequiredOriginList, selectCarrierTypeList)}
                    initialState={{}}
                    isPaginated={false}
                    getRef={ft => {
                      listRef.current = ft;
                    }}
                    rowClassName={(data: BrokerTask) => {
                      return data.priority ? "priority" : "";
                    }}
                    onRowClick={handleRowClick}
                    onUpdate={async () => {
                      setLoading(true);
                      const result = await dispatch(fetchBrokerTasks(brokerID));
                      setLoading(false);
                      const data = get(result, "value.data.items", []) as BrokerTask[];
                      return {
                        total: 0,
                        data,
                      };
                    }}
                  />
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>

      <Form initialValues={task} onSubmit={handleSave}>
        {({ handleSubmit }) => (
          <PromptModal
            open={isModalOpen}
            title={S.BRO_DOC_ADD_TASK_BUTTON}
            confirmText={S.BRO_TASKS_MODAL_SAVE_BUTTON}
            rejectText={S.BRO_TASKS_MODAL_CANCEL_BUTTON}
            onReject={() => setIsModalOpen(false)}
            onConfirm={handleSubmit}>
            <form onSubmit={handleSubmit}>
              <BrokerTasksForm
                selectCarrierTypeList={selectCarrierTypeList}
                selectStatusTypeList={selectStatusTypeList}
                selectRequiredOriginList={selectRequiredOriginList}
                selectTaskCodeTypeList={selectTaskCodeTypeList}
              />
            </form>
          </PromptModal>
        )}
      </Form>
      <Toast open={loading} message={S.LOADING} onClose={() => {}} />
      <Toast
        open={isUploadToastVisible}
        message={S.BRO_DOC_FILE_UPLOAD_SUCCESS_TOAST}
        onClose={() => dispatch(toggleBrokerUploadToast(false))}
      />
    </>
  );
};

export default BrokerTasks;
